import React, { useEffect, useState } from "react";
import { Copy, Trash2, Loader2, CheckCircle2, EyeOff, Undo2 } from "lucide-react";
import api from "../lib/api";
import { useCurrency } from "../lib/contexts";
import { fmtDate } from "../lib/permissions";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";

// Manager-only clean-up for records that were accidentally created twice.
// resource is "deals" (same company + product + code) or "contacts"
// (same company name + contact person).
export default function DuplicatesDialog({ resource, open, onClose, onChanged }) {
  const [groups, setGroups] = useState(null);
  const [ignored, setIgnored] = useState([]);
  const [showIgnored, setShowIgnored] = useState(false);
  const [busy, setBusy] = useState("");
  const { format } = useCurrency();
  const isDeals = resource === "deals";

  const load = () => {
    setGroups(null);
    api.get(`/${resource}/duplicates`).then((r) => setGroups(r.data)).catch(() => setGroups([]));
    if (isDeals) {
      api.get(`/deals/duplicates`, { params: { include_ignored: true } })
        .then((r) => setIgnored(r.data || [])).catch(() => setIgnored([]));
    }
  };
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // "Not a duplicate" — the deals stay in the pipeline, the suggestion goes away.
  const ignoreGroup = async (g) => {
    setBusy(g.group_key);
    try {
      await api.post(`/deals/duplicates/ignore`, {
        contact_id: g.contact_id, product_name: g.product_name, product_code: g.product_code,
      });
      toast.success("Marked as not a duplicate", { description: "Both deals stay in the pipeline." });
      load(); onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not ignore this group"); }
    setBusy("");
  };

  const undoIgnore = async (g) => {
    setBusy(g.group_key);
    try {
      await api.delete(`/deals/duplicates/ignore/${g.ignore_id}`);
      toast.success("Suggestion restored");
      load(); onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not undo"); }
    setBusy("");
  };

  const remove = async (id) => {
    setBusy(id);
    try {
      await api.delete(`/${resource}/${id}`);
      toast.success("Duplicate removed");
      load();
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not delete"); }
    setBusy("");
  };

  const heading = (g) => isDeals
    ? <>{g.company_name} · {g.product_name}
        {g.product_code && <span className="ml-2 text-[10px] font-mono bg-neutral-200 px-1.5 py-0.5 rounded">{g.product_code}</span>}</>
    : <>{g.company_name}{g.contact_person && <span className="font-normal text-muted-foreground"> · {g.contact_person}</span>}</>;

  const summary = (d) => isDeals
    ? <>
        <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-border">{d.stage}</span>
        <span className="font-medium">{d.stage === "Won" ? format(d.actual_value) : format(d.estimated_value)}</span>
        {d.pi_number && <span className="text-[11px] text-muted-foreground">PI {d.pi_number}</span>}
      </>
    : <>
        <span className="font-medium">{d.email || "—"}</span>
        <span className="text-[11px] text-muted-foreground">{d.phone || "—"}</span>
        {(d.products?.length || 0) > 0 && <span className="text-[11px] text-muted-foreground">{d.products.length} product(s)</span>}
      </>;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Copy className="w-5 h-5 text-primary" />Duplicate {isDeals ? "deals" : "contacts"}
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">
          {isDeals
            ? "Deals sharing the same company, product and code. Keep the one with the correct figures and delete the extra."
            : "Contacts sharing the same company name and contact person. Keep the most complete record and delete the extra."}
        </p>

        {groups === null ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
        ) : groups.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-emerald-700 border border-emerald-200 bg-emerald-50/60 rounded-md p-4" data-testid="no-duplicates">
            <CheckCircle2 className="w-4 h-4" />No duplicate {isDeals ? "deals" : "contacts"} found.
          </div>
        ) : (
          <div className="space-y-4" data-testid="duplicate-groups">
            {groups.map((g) => (
              <div key={g.group_key || `${g.company_name}-${g.product_name || g.contact_person}-${g.product_code || ""}`}
                className="border border-border rounded-md overflow-hidden">
                <div className="bg-secondary/60 px-3 py-2 text-sm font-semibold flex items-center justify-between gap-2">
                  <span>{heading(g)}</span>
                  <span className="flex items-center gap-2">
                    <span className="text-[11px] font-normal text-muted-foreground">{g.count} records</span>
                    {isDeals && (
                      <Button variant="outline" size="sm" data-testid={`ignore-group-${g.group_key}`}
                        disabled={busy === g.group_key} onClick={() => ignoreGroup(g)}
                        className="h-7 px-2 text-[11px] font-normal">
                        {busy === g.group_key ? <Loader2 className="w-3 h-3 animate-spin" /> : <><EyeOff className="w-3 h-3 mr-1" />Not a duplicate</>}
                      </Button>
                    )}
                  </span>
                </div>
                <div className="divide-y divide-border">
                  {(isDeals ? g.deals : g.contacts).map((d) => (
                    <div key={d.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">{summary(d)}</div>
                        <div className="text-[11px] text-muted-foreground truncate">
                          Created {fmtDate(d.created_at)} by {d.created_by_name || "—"}
                          {d.notes?.length ? ` · ${d.notes.length} note(s)` : ""}
                        </div>
                      </div>
                      <Button variant="outline" size="sm" data-testid={`delete-duplicate-${d.id}`}
                        disabled={busy === d.id} onClick={() => remove(d.id)}
                        className="h-7 px-2 text-[11px] border-destructive/40 text-destructive hover:bg-destructive/10">
                        {busy === d.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Trash2 className="w-3 h-3 mr-1" />Delete</>}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {isDeals && (
          <div className="border-t border-border pt-3 mt-2">
            <button type="button" data-testid="toggle-ignored" onClick={() => setShowIgnored((v) => !v)}
              className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1.5">
              <EyeOff className="w-3.5 h-3.5" />
              {showIgnored ? "Hide" : "Show"} ignored ({ignored.length})
            </button>
            {showIgnored && (
              <div className="mt-2 space-y-2" data-testid="ignored-groups">
                {ignored.length === 0 && (
                  <div className="text-xs italic text-muted-foreground">Nothing has been set aside yet.</div>
                )}
                {ignored.map((g) => (
                  <div key={g.group_key} className="border border-dashed border-border rounded-md px-3 py-2 flex items-center justify-between gap-3 text-sm bg-secondary/30">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{heading(g)}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {g.count} records · set aside by {g.ignored_by_name || "—"} on {fmtDate(g.ignored_at)}
                      </div>
                    </div>
                    <Button variant="outline" size="sm" data-testid={`undo-ignore-${g.group_key}`}
                      disabled={busy === g.group_key} onClick={() => undoIgnore(g)}
                      className="h-7 px-2 text-[11px]">
                      {busy === g.group_key ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Undo2 className="w-3 h-3 mr-1" />Undo</>}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
